import { initializePaddle, Paddle, PaddleEventData } from '@paddle/paddle-js'
import getConfig from 'next/config'
import React, { ReactNode, useEffect, useState } from 'react'

import { getUserSubscriptionInnerRoute, getUserUsageInnerRoute, isUserSubscribedInnerRoute } from '../api/auth'
import {
  changePaddleBillingSubscriptionPlanInnerRoute,
  getPaddleBillingCustomerIdInnerRoute,
  getPaddleBillingPlanConfigs,
} from '../api/paddleBilling'
import PaymenResultModal, {
  PaymentResultStatus,
} from '../components/common/MarketingPayment/PaymenResultModal/PaymenResultModal'
import { trackStartTrial } from '../utils/gtag/gtagMetricsUtils'
import { FrontendUserDetailed } from '../utils/types'
import { useCurrentUser } from './CurrentUserContext'
import { useInternationalization } from './InternationalizationContext'

type TPlanConfigs = { [key: string]: { oldPrice: number; newPrice: number; currency: string; priceId: string } }
const freePlanConfig: TPlanConfigs = { free: { oldPrice: 0, newPrice: 0, currency: '$', priceId: '' } }

const { publicRuntimeConfig } = getConfig() || {}
const isDev = publicRuntimeConfig.SERVER_ENV === 'development'
const checkoutFrameTargetClassName = 'paddle-billing-checkout-frame-target'

type FetchType = {
  checkoutFrameTargetClassName: string
  setCheckoutItems: (priceIds: string[]) => void
  openCheckout: (priceIds?: string[]) => void
  runAsyncWithLoader: (fn: any) => Promise<void>
  handleUpgradePlan: () => Promise<void>
  //
  planConfigs: TPlanConfigs
  isYearly: boolean
  setIsYearly: (v: boolean | ((v: boolean) => boolean)) => void
  selectedPlan: string | null
  setSelectedPlan: (v: string | null) => void
  //
  getPlanName: (planKey: string) => string
  getLimitName: (limitKey: string) => string
  getLimitDescription: (limitKey: string) => string | undefined
  getLimitData: (limitKey: string, ignoreFrontend?: boolean) => { current: number; limit: number }
  updateLimitData: () => Promise<void>
}

export const PaddleBillingContext = React.createContext<FetchType>({
  checkoutFrameTargetClassName,
  setCheckoutItems: () => {},
  openCheckout: () => {},
  runAsyncWithLoader: () => new Promise(() => {}),
  handleUpgradePlan: () => new Promise(() => {}),
  //
  planConfigs: {},
  isYearly: false,
  setIsYearly: () => {},
  selectedPlan: null,
  setSelectedPlan: () => {},
  //
  getPlanName: () => '',
  getLimitName: () => '',
  getLimitDescription: () => '',
  getLimitData: () => ({ current: 0, limit: 0 }),
  updateLimitData: () => new Promise(() => {}),
})

export const PaddleBillingProvider = ({ children }: { children: ReactNode }) => {
  const { currentUser } = useCurrentUser() as { currentUser: FrontendUserDetailed | null }
  const { normalizedLocale, addComponentText, getText, isTextReady } = useInternationalization()

  const [paddle, setPaddle] = useState<Paddle | undefined>(undefined)
  const [items, setItems] = useState<{ priceId: string }[]>([])

  const [paymentModalOpen, setPaymentModalOpen] = useState(false)
  const [paymentModalStatus, setPaymentModalStatus] = useState<PaymentResultStatus>('pending')

  const [isYearly, setIsYearly] = useState(true)
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null)
  const [planConfigs, setPlanConfigs] = useState<TPlanConfigs>(freePlanConfig)

  const [userUsage, setUserUsage] = useState<any>(undefined)

  const STRINGS = getText('PaddleBillingContext') || {}
  const isStringsReady = isTextReady(STRINGS)

  useEffect(() => {
    if (!currentUser) return

    initializePaddle({
      environment: isDev ? 'sandbox' : 'production',
      token: publicRuntimeConfig.PADDLE_CLIENT_SIDE_TOKEN,
      eventCallback,
    }).then((paddleInstance) => setPaddle(paddleInstance))
  }, [])
  useEffect(() => {
    if (!currentUser) return

    const priceIds = [
      isDev ? 'pri_01jwhbaw3jg5ty9bh79qba6ah9' : 'pri_01jx36gm7dqfhdky5g07pqdk94', // creator_monthly
      isDev ? 'pri_01jwhccb7x92a4bded12q7xq9s' : 'pri_01jx36j0wkrrj22rjf4jr2ajeq', // creator_yearly
      isDev ? 'pri_01jwhcerb9rzc4qbx3txn6rmk0' : 'pri_01jx36kr9a7z96h127e29yenhv', // pro_monthly
      isDev ? 'pri_01jwhcfz1ebrayry2pjdm5r4ca' : 'pri_01jx36mwwt82yxsx827fddm5d3', // pro_yearly
    ]

    getPaddleBillingPlanConfigs(priceIds).then((res) => {
      setPlanConfigs({
        ...freePlanConfig,
        ...res,
      })
    })
  }, [])
  useEffect(() => {
    addComponentText('PaddleBillingContext')
  }, [])
  useEffect(() => {
    updateLimitData()
  }, [])

  const eventCallback = async (event: PaddleEventData) => {
    if (!currentUser) return

    switch (event.name) {
      case 'checkout.loaded':
        paddle?.Spinner.hide()
        break
      case 'checkout.completed': {
        try {
          fpr('referral', { email: currentUser.email })
          trackStartTrial(currentUser.id)
        } catch (e) {
          console.error(e)
        }

        setPaymentModalOpen(true)
        setPaymentModalStatus('pending')

        const interval = setInterval(async () => {
          const isSubscribed = await isUserSubscribedInnerRoute()
          if (isSubscribed) {
            clearTimeout(timeout)
            clearInterval(interval)
            document.location.reload()
          }
        }, 500)
        const timeout = setTimeout(async () => {
          clearInterval(interval)
          setPaymentModalOpen(false)
          setPaymentModalStatus('error')
        }, 60 * 1000)

        break
      }
      default:
        break
    }
  }
  const runAsyncWithLoader = async (fn: any) => {
    setPaymentModalOpen(true)
    setPaymentModalStatus('pending')

    await fn()

    setPaymentModalStatus('success')
    setPaymentModalOpen(false)
  }
  const handleUpgradePlan = async () => {
    if (!selectedPlan) return

    const { priceId } = planConfigs[selectedPlan]

    setPaymentModalOpen(true)
    setPaymentModalStatus('pending')

    await changePaddleBillingSubscriptionPlanInnerRoute(priceId)

    const interval = setInterval(async () => {
      const subscriptionPlan = await getUserSubscriptionInnerRoute()
      const isSubscriptionUgraded = subscriptionPlan === selectedPlan

      console.log(subscriptionPlan, selectedPlan)

      if (isSubscriptionUgraded) {
        clearTimeout(timeout)
        clearInterval(interval)
        document.location.reload()
      }
    }, 500)
    const timeout = setTimeout(async () => {
      clearInterval(interval)
      setPaymentModalOpen(false)
      setPaymentModalStatus('error')
    }, 60 * 1000)
  }

  const setCheckoutItems = (priceIds: string[]) => {
    const formatedPriceIds = priceIds.filter(Boolean).map((priceId) => ({ priceId }))
    setItems(formatedPriceIds)
    return formatedPriceIds
  }
  const openCheckout = async (priceIds?: string[]) => {
    const itemsLocal = priceIds ? setCheckoutItems(priceIds) : items

    if (!paddle || !currentUser || !itemsLocal.length) return

    // @ts-ignore
    let paddleCustomerId = '' // currentUser.paddleBillingCustomerId
    if (!paddleCustomerId) paddleCustomerId = await getPaddleBillingCustomerIdInnerRoute()

    paddle.Checkout.open({
      items: itemsLocal,

      settings: {
        allowLogout: false,
        //
        displayMode: 'inline',
        frameTarget: checkoutFrameTargetClassName, // Class name of the <div> element where the checkout should be rendered.
        frameInitialHeight: 300,
        frameStyle: 'width:100%; min-width:312px; background-color: transparent; border: none;',
        locale: normalizedLocale,
        //
        showAddDiscounts: false,
        successUrl: undefined, // prevent instand redirect
        // theme: 'dark',
      },

      customer: {
        id: paddleCustomerId,
      },

      customData: {
        userId: currentUser.id,
        email: currentUser.email,
      },
    })

    paddle.Spinner.show()
  }

  const getPlanName = (planKey: string) => {
    if (!isStringsReady) return ''
    return STRINGS.planNames[planKey]
  }
  const getLimitName = (limitKey: string) => {
    if (!isStringsReady) return ''
    return STRINGS.limitNames[limitKey]
  }
  const getLimitDescription = (limitKey: string) => {
    if (!isStringsReady) return ''
    return STRINGS.limitDescriptions[limitKey]
  }

  const updateLimitData = async () => {
    await getUserUsageInnerRoute().then((data) => setUserUsage(data))
  }
  const getLimitData = (limitKey: string, ignoreFrontend = false): { current: number; limit: number } => {
    const frontEndLimits = [
      'midi_output',
      'guitar_chords',
      'projects',
      'song_sections',
      'song_track_layers',
      'song_section_chords',
      'drums_autocomplete',
      'chord_palette',
    ]
    const isFrontendLimit = frontEndLimits.includes(limitKey)

    if (!userUsage) return { current: -1, limit: -1 }

    const { current, limit } = userUsage[limitKey] || { current: -1, limit: -1 }
    return { current: isFrontendLimit && !ignoreFrontend ? limit : current, limit }
  }

  return (
    <PaddleBillingContext.Provider
      value={{
        checkoutFrameTargetClassName,
        setCheckoutItems,
        openCheckout,
        runAsyncWithLoader,
        handleUpgradePlan,
        //
        planConfigs,
        isYearly,
        setIsYearly,
        selectedPlan,
        setSelectedPlan,
        //
        getPlanName,
        getLimitName,
        getLimitDescription,
        getLimitData,
        updateLimitData,
      }}
    >
      <PaymenResultModal isOpen={paymentModalOpen} onClose={() => {}} status={paymentModalStatus} />
      {children}
    </PaddleBillingContext.Provider>
  )
}

export const usePaddleBilling = () => React.useContext<FetchType>(PaddleBillingContext)
